import type {
  BrandAssistantSelectedAssetReference,
  BrandAssistantSelectedImageReference,
} from "./brand-assistant-chat-request";

type SelectedAssetReferenceInput = {
  generatedAssetId: string;
  batchId: string;
  batchSourceType?: "standalone" | "campaign" | null;
  batchName: string;
  scheduledPostId: string | null;
  blockId: string;
  position: number;
  slideIndex: number | null;
  slideId: string | null;
  prompt: string | null;
  caption?: string | null;
  hashtags?: string[] | null;
  signedUrl: string;
  post?: { draftUpdatedAt?: string | null } | null;
};

type SelectedImageReferenceInput = {
  generatedAssetId: string;
  batchId: string;
  prompt: string | null;
  aspectRatio: BrandAssistantSelectedImageReference["aspectRatio"];
  signedUrl: string;
};

const SELECTED_REFERENCE_BATCH_NAME_MAX_LENGTH = 200;
const SELECTED_REFERENCE_BLOCK_ID_MAX_LENGTH = 120;
const SELECTED_REFERENCE_PROMPT_MAX_LENGTH = 2_000;
const SELECTED_REFERENCE_CAPTION_MAX_LENGTH = 2_200;
const SELECTED_REFERENCE_HASHTAG_MAX_LENGTH = 80;
const SELECTED_REFERENCE_SLOT_LABEL_MAX_LENGTH = 120;
const SELECTED_REFERENCE_SLOT_HINT_MAX_LENGTH = 120;
const SELECTED_REFERENCE_SLIDE_ID_MAX_LENGTH = 120;

export function buildSelectedAssetReference(
  asset: SelectedAssetReferenceInput,
): BrandAssistantSelectedAssetReference {
  return {
    generatedAssetId: asset.generatedAssetId,
    batchId: asset.batchId,
    batchSourceType: asset.batchSourceType ?? null,
    batchName: normalizeRequiredText(
      asset.batchName,
      "Generated assets",
      SELECTED_REFERENCE_BATCH_NAME_MAX_LENGTH,
    ),
    scheduledPostId: asset.scheduledPostId,
    blockId: normalizeRequiredText(asset.blockId, "image", SELECTED_REFERENCE_BLOCK_ID_MAX_LENGTH),
    ...getSelectedAssetImageTargetMetadata(asset),
    slideIndex: normalizePositiveInteger(asset.slideIndex),
    slideId: normalizeOptionalText(asset.slideId, SELECTED_REFERENCE_SLIDE_ID_MAX_LENGTH),
    prompt: normalizeOptionalText(asset.prompt, SELECTED_REFERENCE_PROMPT_MAX_LENGTH),
    caption: normalizeOptionalText(asset.caption, SELECTED_REFERENCE_CAPTION_MAX_LENGTH),
    hashtags: normalizeOptionalStringArray(
      asset.hashtags,
      30,
      SELECTED_REFERENCE_HASHTAG_MAX_LENGTH,
    ),
    draftUpdatedAt: asset.post?.draftUpdatedAt ?? null,
    signedUrl: asset.signedUrl,
  };
}

export function buildSelectedImageReference(
  image: SelectedImageReferenceInput,
): BrandAssistantSelectedImageReference {
  return {
    generatedAssetId: image.generatedAssetId,
    batchId: image.batchId,
    prompt: normalizeOptionalText(image.prompt, SELECTED_REFERENCE_PROMPT_MAX_LENGTH),
    aspectRatio: image.aspectRatio,
    signedUrl: image.signedUrl,
  };
}

function getSelectedAssetImageTargetMetadata(
  asset: Pick<SelectedAssetReferenceInput, "blockId" | "position" | "slideIndex">,
): Pick<
  BrandAssistantSelectedAssetReference,
  "imageSlotLabel" | "imagePositionHint" | "imageSlotIndex"
> {
  const slideIndex = normalizePositiveInteger(asset.slideIndex);
  const normalizedBlockId = asset.blockId.toLowerCase();
  if (normalizedBlockId === "hero_image" || normalizedBlockId === "hero-image") {
    return {
      imageSlotLabel: normalizeRequiredText(
        "Lead image",
        "Image block",
        SELECTED_REFERENCE_SLOT_LABEL_MAX_LENGTH,
      ),
      imagePositionHint: slideIndex
        ? null
        : normalizeOptionalText("top or upper image", SELECTED_REFERENCE_SLOT_HINT_MAX_LENGTH),
      imageSlotIndex: 1,
    };
  }

  if (
    normalizedBlockId === "supporting_image" ||
    normalizedBlockId === "supporting_images" ||
    normalizedBlockId === "supporting-image" ||
    normalizedBlockId === "supporting-images"
  ) {
    return {
      imageSlotLabel: normalizeRequiredText(
        "Supporting image",
        "Image block",
        SELECTED_REFERENCE_SLOT_LABEL_MAX_LENGTH,
      ),
      imagePositionHint: slideIndex
        ? null
        : normalizeOptionalText(
            "second, bottom, or lower image",
            SELECTED_REFERENCE_SLOT_HINT_MAX_LENGTH,
          ),
      imageSlotIndex: 2,
    };
  }

  return {
    imageSlotLabel: normalizeRequiredText(
      "Image block",
      "Image block",
      SELECTED_REFERENCE_SLOT_LABEL_MAX_LENGTH,
    ),
    imagePositionHint: null,
    imageSlotIndex: Math.max(1, Math.trunc(asset.position) + 1),
  };
}

function normalizeRequiredText(
  value: string | null | undefined,
  fallback: string,
  maxLength: number,
) {
  return normalizeOptionalText(value, maxLength) ?? fallback.slice(0, maxLength);
}

function normalizeOptionalText(value: string | null | undefined, maxLength: number) {
  const trimmed = value?.trim();
  return trimmed ? trimmed.slice(0, maxLength) : null;
}

function normalizeOptionalStringArray(
  value: readonly string[] | null | undefined,
  maxItems: number,
  maxLength: number,
) {
  if (!value) {
    return null;
  }

  const normalized = value
    .map((item) => normalizeOptionalText(item, maxLength))
    .filter((item): item is string => Boolean(item))
    .slice(0, maxItems);

  return normalized.length > 0 ? normalized : [];
}

function normalizePositiveInteger(value: number | null | undefined) {
  if (typeof value !== "number") {
    return null;
  }

  return Number.isInteger(value) && value > 0 ? value : null;
}
